import { useCallback, useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Download } from "lucide-react";
import { getFinancialAuditLog, downloadCsv, type FinancialAuditRow } from "@/lib/financialLedger";

const AREAS = [
  { value: "all", label: "Everything" },
  { value: "ledger", label: "Ledger entries" },
  { value: "manual_entry", label: "Manual entries" },
  { value: "payroll", label: "Payroll" },
  { value: "grace", label: "Grace payments" },
  { value: "reconciliation", label: "Reconciliations" },
  { value: "month_close", label: "Month closes" },
  { value: "snapshot", label: "Daily snapshots" },
];

const short = (v: any) => {
  if (v === null || v === undefined) return "—";
  const s = typeof v === "string" ? v : JSON.stringify(v);
  return s.length > 120 ? `${s.slice(0, 117)}…` : s;
};

export function FinancialAuditLogDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const [area, setArea] = useState("all");
  const [rows, setRows] = useState<FinancialAuditRow[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getFinancialAuditLog({ area, limit: 300 });
      setRows(r.rows);
    } finally {
      setLoading(false);
    }
  }, [area]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const exportCsv = () => {
    const esc = (v: string) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = ["When,Area,Action,Record,Who,Reason,Changed Fields,Previous,New"];
    for (const r of rows) {
      lines.push([
        new Date(r.occurred_at).toISOString(), r.area, r.action,
        `${r.record_table ?? ""} ${r.record_id ?? ""}`.trim(), r.actor_label ?? "System",
        r.reason ?? "", (r.changed_fields ?? []).join(" "),
        JSON.stringify(r.previous_value ?? null), JSON.stringify(r.new_value ?? null),
      ].map(esc).join(","));
    }
    downloadCsv(`silver-sage-financial-audit-log-${new Date().toISOString().slice(0, 10)}.csv`, lines.join("\n"));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Financial audit log</DialogTitle>
          <DialogDescription>
            A permanent record of every financial action — who did it, what changed, the previous and new values, when,
            and why. Entries can never be edited or deleted.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2">
          <Select value={area} onValueChange={setArea}>
            <SelectTrigger className="w-[220px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {AREAS.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={exportCsv} disabled={!rows.length}>
            <Download className="h-4 w-4 mr-1" /> Export
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading the audit trail…
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8">No financial actions recorded for this filter yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Area</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Who</TableHead>
                  <TableHead>Changed</TableHead>
                  <TableHead>Previous</TableHead>
                  <TableHead>New</TableHead>
                  <TableHead>Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="whitespace-nowrap text-xs">
                      {new Date(r.occurred_at).toLocaleString("en-US")}
                    </TableCell>
                    <TableCell className="text-xs"><Badge variant="secondary">{r.area.replace(/_/g, " ")}</Badge></TableCell>
                    <TableCell className="text-xs">{r.action.replace(/_/g, " ")}</TableCell>
                    <TableCell className="text-xs">{r.actor_label ?? "System"}</TableCell>
                    <TableCell className="text-xs max-w-[160px] truncate">{(r.changed_fields ?? []).join(", ") || "—"}</TableCell>
                    <TableCell className="text-[11px] text-muted-foreground max-w-[200px] truncate">{short(r.previous_value)}</TableCell>
                    <TableCell className="text-[11px] text-muted-foreground max-w-[200px] truncate">{short(r.new_value)}</TableCell>
                    <TableCell className="text-xs max-w-[200px] truncate">{r.reason ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
